import axios, { AxiosError } from "axios";
import {useQueryClient} from "@tanstack/react-query";
import {useAddRoleMaster, useDeleteRoleMaster, useRoleMaster} from "./queries";
import {useMenuMaster} from "../LegacyMenuMasterConfig/queries";
import React, {useMemo, useState} from "react";
import Swal from "sweetalert2";
import {Button, FormGroup, Input, Label} from "reactstrap";
import { FaPlus } from "react-icons/fa";
import Select from 'react-select';
import {RoleMaster, RoleMasterForm} from "../../models/config/RoleMaster";
import {useCompanyMaster} from "../../queries";
import { CompanyMaster } from "models/companymaster/CompanyMaster";
import { useBoolean } from "usehooks-ts";
import { extractAccessToken } from "helpers/jwt";
import {RoleMasterFormModal, RoleMasterTable} from "./components";
import { useUserInformation } from "hooks";

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const LegacyRoleMasterConfig = () => {
    const queryClient = useQueryClient();

    const { data: roleMaster } = useRoleMaster();

    const { data: menuMaster } = useMenuMaster();
    const menuMasterMap = useMemo(
        () =>
            menuMaster
                ? new Map(menuMaster.map(menu => [menu.id, menu]))
                : undefined,
        [menuMaster]
    );

    const roleMasterForms = useMemo(() => {
        if (!menuMasterMap || !roleMaster) {
            return undefined;
        }

        const output = new Map<string, RoleMasterForm>();
        roleMaster.forEach(role => {
            const menu = menuMasterMap.get(role.menu_id);
            if (!menu) {
                return;
            }

            const roleMasterForm = output.get(role.sid + role.id);
            if (!roleMasterForm) {
                output.set(role.sid + role.id, {
                    sid: role.sid,
                    id: role.id,
                    roleDetail: role.role_detail,
                    allowMenus: [menu]
                });
            } else {
                roleMasterForm.allowMenus.push(menu);
            }
        });

        return Array.from(output.values());
    }, [menuMasterMap, roleMaster]);

	const { companyOptions } = useUserInformation()
    const { data: companyMaster } = useCompanyMaster();

    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();
    const [searchRoleId, setSearchRoleId] = useState('');

    const filteredMasterRoleForms = useMemo(
        () =>
            roleMasterForms?.filter(roleMasterForm => {
                const isMatchedSID =
                    selectedCompany && selectedCompany.sid !== 'all'
                        ? roleMasterForm.sid === selectedCompany.sid
                        : true
                const isMatchedRoleId = roleMasterForm.id
                    .toLowerCase()
                    .includes(searchRoleId.toLowerCase());

                return isMatchedSID && isMatchedRoleId;
            }),
        [
            companyMaster,
            roleMasterForms,
            selectedCompany,
            searchRoleId
        ]
    );

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();

    const [selectedEditRoleMasterForm, setSelectedEditRoleMasterForm] =
        useState<RoleMasterForm>();
    const isOpenEditModal = !!selectedEditRoleMasterForm;

    const { mutate: addRoleMaster, mutateAsync: addRoleMasterAsync } =
        useAddRoleMaster();
    const { mutate: deleteRoleMaster, mutateAsync: deleteRoleMasterAsync } =
        useDeleteRoleMaster();

    const handleSubmitAddRoleMaster = (formValues: RoleMasterForm) => {
        const payload = extractAccessToken();

        const createdBy = payload.citizen_id || payload.username;
        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    role_detail: formValues.roleDetail,
                    menu_id: menu.id,
                    created_by: createdBy
                }) as RoleMaster
        );

        requests.forEach(request =>
            addRoleMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    toggleIsOpenCreateModal();

                    await queryClient.invalidateQueries({
                        queryKey: ['role-master']
                    });
                }
            })
        );
    };

    const handleEditRoleMasterForm = (roleMasterForm: RoleMasterForm) => {
        setSelectedEditRoleMasterForm(roleMasterForm);
    };

    const handleCloseEditModal = () => {
        setSelectedEditRoleMasterForm(undefined);
    };

    const handleSubmitEditRoleMaster = async (formValues: RoleMasterForm) => {
        const currentMasterRoleForm = roleMasterForms?.find(
            roleMasterForm => roleMasterForm.id === formValues.id
        );
        if (!currentMasterRoleForm) {
            return;
        }

        const currentAllowedMenus = currentMasterRoleForm.allowMenus;

        // find remove menus
        const newAllowedMenuIdSet = new Set(
            formValues.allowMenus.map(menu => menu.id)
        );
        const removeRoleMasterRequests = currentAllowedMenus
            .filter(menu => !newAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id
                    }) as RoleMaster
            );

        // find newly added munus
        const payload = extractAccessToken();
        const currentAllowedMenuIdSet = new Set(
            currentAllowedMenus.map(menu => menu.id)
        );
        const addRoleMasterRequests = formValues.allowMenus
            .filter(menu => !currentAllowedMenuIdSet.has(menu.id))
            .map(
                menu =>
                    ({
                        sid: formValues.sid,
                        id: formValues.id,
                        menu_id: menu.id,
                        role_detail: formValues.roleDetail,
                        created_by: payload.citizen_id || payload.username
                    }) as RoleMaster
            );

        try {
            await Promise.all([
                ...removeRoleMasterRequests.map(request =>
                    deleteRoleMasterAsync(request)
                ),
                ...addRoleMasterRequests.map(request =>
                    addRoleMasterAsync(request)
                )
            ]);

            handleCloseEditModal();

            await Swal.fire({
                title: 'Success!',
                icon: 'success'
            });
            await queryClient.invalidateQueries({ queryKey: ['role-master'] });
        } catch (error) {
            await Swal.fire({
                title: 'Error!',
                icon: 'error',
                text: getErrorMessage(error)
            });
        }
    };

    const handleDeleteRoleMaster = async (formValues: RoleMasterForm) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${formValues.id}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }

        const requests = formValues.allowMenus.map(
            menu =>
                ({
                    sid: formValues.sid,
                    id: formValues.id,
                    menu_id: menu.id
                }) as RoleMaster
        );

        requests.forEach(request =>
            deleteRoleMaster(request, {
                onSuccess: async () => {
                    await Swal.fire({
                        title: 'Success!',
                        icon: 'success'
                    });
                },
                onError: async error => {
                    await Swal.fire({
                        title: 'Error!',
                        icon: 'error',
                        text: getErrorMessage(error)
                    });
                },
                onSettled: async () => {
                    await queryClient.invalidateQueries({
                        queryKey: ['role-master']
                    });
                }
            })
        );
    };

    return (
        <div className="master-data-content tw-flex tw-flex-1 tw-flex-col">
            <div className="tw-flex tw-justify-between">
                <div className="trapezoid-head tw-w-fit">
                    <span>Workflow Role Master Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="tw-flex tw-items-center tw-gap-1 tw-self-center"
                >
                    <FaPlus/>
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box tw-flex tw-flex-1 tw-flex-col">
                {/* Search */}
                <div className="tw-flex tw-justify-between">
                    <div className="trapezoid-head tw-w-fit">
                        <span>Search</span>
                    </div>
                </div>
                <div className="mat-box tw-flex tw-flex-col">
                    <FormGroup className="tw-w-full md:tw-w-1/3">
                        <Label for="sid">Company</Label>
                        <Select
                            options={companyOptions}
                            value={selectedCompany}
                            getOptionLabel={(company: CompanyMaster) =>
                                company.company_name
                            }
                            getOptionValue={(company: CompanyMaster) =>
                                company.sid
                            }
                            onChange={setSelectedCompany}
                        />
                    </FormGroup>
                    <FormGroup className="tw-w-full md:tw-w-1/3">
                        <Label for="id">Role ID</Label>
                        <Input
                            type="text"
                            value={searchRoleId}
                            onChange={event =>
                                setSearchRoleId(event.target.value)
                            }
                        />
                    </FormGroup>
                </div>

                <RoleMasterTable
                    roleMasterForms={filteredMasterRoleForms}
                    onEdit={handleEditRoleMasterForm}
                    onDelete={handleDeleteRoleMaster}
                />

                {/*  Create modal */}
                <RoleMasterFormModal
                    title="Create Role"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddRoleMaster}
                />

                {/* Edit modal */}
                <RoleMasterFormModal
                    disabledKeyField
                    title="Edit Role"
                    defaultValues={selectedEditRoleMasterForm}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditRoleMaster}
                />
            </div>
        </div>
    );
}